import { useSyncExternalStore } from 'react';
import { api, socket } from './api';
import { lastPosition, setPanicTracking } from './device';

/**
 * Silent alert (PNC-001, docs/panic.md): the trigger module, apart from any button. The top bar's
 * connection status calls it today; the Android shell may call it from the volume button later
 * (`trigger('volume')`), with the same grace and the same confirmation.
 *
 *   idle --trigger--> grace (5 s, one tap undoes it) --> active (sent, or waiting in the queue)
 *   active --reset--> idle (the driver turns it off: false alarm)
 *
 * Nothing on the screen is red or sounds: the only sign is the square dot of the connection status.
 * Without signal the alert waits in IndexedDB (it survives a reload or a closed app) and is retried
 * when the socket comes back, when the browser is online again and every RETRY_MS; it keeps the time
 * it was raised. Practice mode walks the same steps without calling the API.
 */

export type PanicSource = 'bar' | 'volume';
export interface PanicState {
  phase: 'idle' | 'grace' | 'active';
  /** seconds left to undo it (grace only) */
  grace: number;
  /** raised but not yet received by the central (no signal) */
  queued: boolean;
  practice: boolean;
  /** a practice just ended well: the security page says so */
  practiced: boolean;
}

const GRACE_S = 5;
const RETRY_MS = 10_000;
/** While active, the app asks whether the central closed it (the driver is never pushed anything). */
const CHECK_MS = 30_000;

let state: PanicState = { phase: 'idle', grace: 0, queued: false, practice: false, practiced: false };
const listeners = new Set<() => void>();
const set = (patch: Partial<PanicState>) => {
  state = { ...state, ...patch };
  setPanicTracking(state.phase === 'active' && !state.practice);
  listeners.forEach((l) => l());
};
export const usePanic = () =>
  useSyncExternalStore(
    (l) => (listeners.add(l), () => void listeners.delete(l)),
    () => state,
  );

// ---------------------------------------------------------------- local queue (IndexedDB)
interface Pending {
  /** what still has to reach the central */
  op: 'raise' | 'cancel';
  raisedAt: string;
  /** whose alert it is: another account signed in on this phone never sends it */
  userId: string;
}
const DB = 'driver-panic';
const KEY = 'pending';
let memory: Pending | null = null;

function store<T>(mode: IDBTransactionMode, run: (s: IDBObjectStore) => IDBRequest<T>): Promise<T | undefined> {
  return new Promise((resolve) => {
    try {
      const open = indexedDB.open(DB, 1);
      open.onupgradeneeded = () => open.result.createObjectStore('kv');
      open.onerror = () => resolve(undefined);
      open.onsuccess = () => {
        const tx = open.result.transaction('kv', mode);
        const req = run(tx.objectStore('kv'));
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => resolve(undefined);
        tx.oncomplete = () => open.result.close();
      };
    } catch {
      resolve(undefined); // private window or storage blocked: the copy in memory still retries
    }
  });
}
async function savePending(p: Pending | null) {
  memory = p;
  await store<unknown>('readwrite', (s) => (p ? s.put(p, KEY) : s.delete(KEY)) as IDBRequest<unknown>);
}
let userId = '';
async function loadPending(): Promise<Pending | null> {
  const p = memory ?? ((await store<Pending>('readonly', (s) => s.get(KEY))) || null);
  return p && p.userId === userId ? p : null;
}

// ---------------------------------------------------------------- talking to the central
/** Network or server trouble: worth retrying. A 4xx answer will not change by retrying. */
const retriable = (err: unknown) => {
  const status = (err as { status?: number }).status ?? 0;
  return status === 0 || status === 429 || status >= 500;
};

let flushing = false;
async function flush() {
  if (flushing) return;
  const p = await loadPending();
  if (!p) return;
  flushing = true;
  try {
    if (p.op === 'raise') {
      const pos = lastPosition();
      await api.post('/driver/panic', { raisedAt: p.raisedAt, ...(pos ? { lat: pos.lat, lng: pos.lng, accuracyM: pos.accuracyM } : {}) });
    } else {
      await api.post('/driver/panic/cancel', {});
    }
    await savePending(null);
    if (state.phase === 'active' && p.op === 'raise') set({ queued: false });
  } catch (err) {
    if (!retriable(err)) {
      await savePending(null);
      if (p.op === 'raise') set({ phase: 'idle', queued: false });
    }
  } finally {
    flushing = false;
  }
}

let graceTimer: ReturnType<typeof setInterval> | null = null;
const stopGrace = () => {
  if (graceTimer) clearInterval(graceTimer);
  graceTimer = null;
};

/** Starts the 5 s to undo it; at 0 the alert goes out (or, practising, pretends to). */
export function trigger(_source: PanicSource) {
  if (state.phase !== 'idle') return;
  set({ phase: 'grace', grace: GRACE_S, practiced: false });
  graceTimer = setInterval(() => {
    if (state.grace > 1) return set({ grace: state.grace - 1 });
    stopGrace();
    void send();
  }, 1000);
}

/** One tap while it says «Actualizando…»: nothing was sent. */
export function undo() {
  if (state.phase !== 'grace') return;
  stopGrace();
  set({ phase: 'idle', grace: 0 });
}

async function send() {
  // the one confirmation: a short vibration (the Android shell's WebView honours it too)
  navigator.vibrate?.(40);
  if (state.practice) return set({ phase: 'active', grace: 0 });
  set({ phase: 'active', grace: 0, queued: true });
  await savePending({ op: 'raise', raisedAt: new Date().toISOString(), userId });
  await flush();
}

/** «Restablecer conexión normal»: the driver turns it off. */
export async function reset() {
  if (state.phase !== 'active') return;
  if (state.practice) return set({ phase: 'idle', practice: false, practiced: true });
  const p = await loadPending();
  set({ phase: 'idle', queued: false });
  // it never left the phone: forgetting it is enough
  if (p?.op === 'raise') return savePending(null);
  await savePending({ op: 'cancel', raisedAt: new Date().toISOString(), userId });
  await flush();
}

/** The security page's «Practicar»: the next trigger only walks the steps. */
export function startPractice() {
  if (state.phase !== 'idle') return;
  set({ practice: true, practiced: false });
}
export function stopPractice() {
  stopGrace();
  if (state.practice) set({ phase: 'idle', grace: 0, practice: false });
}

/** After sign-in and every reconnection: what is still queued goes out; what the central has decides the state. */
async function sync() {
  await flush();
  if (state.practice || state.phase === 'grace') return;
  const p = await loadPending();
  if (p?.op === 'raise') return set({ phase: 'active', queued: true });
  if (p?.op === 'cancel') return set({ phase: 'idle' });
  try {
    const r = await api.get<{ alert: { status: string } | null }>('/driver/panic');
    set(r.alert ? { phase: 'active', queued: false } : state.phase === 'active' ? { phase: 'idle', queued: false } : {});
  } catch {
    /* offline: keep what we have */
  }
}

/** Wires the retries for the signed-in driver; returns the clean-up for sign-out. */
export function startPanic(user: string): () => void {
  userId = user;
  memory = null;
  void sync();
  const offState = socket.onState((s) => s === 'open' && void sync());
  const onOnline = () => void flush();
  window.addEventListener('online', onOnline);
  const retry = setInterval(() => void flush(), RETRY_MS);
  const check = setInterval(() => state.phase === 'active' && !state.practice && !state.queued && void sync(), CHECK_MS);
  return () => {
    offState();
    window.removeEventListener('online', onOnline);
    clearInterval(retry);
    clearInterval(check);
    stopGrace();
    set({ phase: 'idle', grace: 0, queued: false, practice: false });
  };
}
